import { Show, ShowMetaDataType } from '../types/Show';

export const FALLBACK_SHOW_THUMBNAIL_URL = '/dashreels_logo.png';

const CARD_THUMBNAIL_TYPES = [
  ShowMetaDataType.REELS_THUMBNAIL_5x8,
  ShowMetaDataType.REELS_THUMBNAIL_4X5,
  ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2,
  ShowMetaDataType.WIDGET_THUMBNAIL_V2,
  ShowMetaDataType.BANNER_THUMBNAIL_V2,
  ShowMetaDataType.DETAIL_PAGE_THUMBNAIL_V2,
  ShowMetaDataType.THUMBNAIL_24X7,
];

export const getShowThumbnailUrl = (
  show: Pick<Show, 'metadata' | 'thumbnailVariants' | 'thumbNailUrl'>,
  fallbackUrl: string = FALLBACK_SHOW_THUMBNAIL_URL
): string => {
  for (const type of CARD_THUMBNAIL_TYPES) {
    const variants = show.thumbnailVariants?.[type];
    const selectedVariant = variants?.find(variant => variant.isActive && variant.assetUrl) || variants?.find(variant => variant.assetUrl);

    if (selectedVariant?.assetUrl) {
      return selectedVariant.assetUrl;
    }
  }

  const metadataThumbnail =
    show.metadata?.find(metadata => metadata.type === ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2)?.value ||
    show.metadata?.find(metadata => metadata.type === ShowMetaDataType.REELS_THUMBNAIL_5x8)?.value ||
    show.metadata?.find(metadata => metadata.type === ShowMetaDataType.REELS_THUMBNAIL_4X5)?.value;

  return metadataThumbnail || show.thumbNailUrl || fallbackUrl;
};

// Aspect ratios for different thumbnail types
export const THUMBNAIL_DIMENSIONS: Record<string, { width: number; height: number; aspectRatio: string }> = {
  [ShowMetaDataType.BANNER_THUMBNAIL_V2]: { width: 1600, height: 2248, aspectRatio: '4:5' },
  [ShowMetaDataType.WIDGET_THUMBNAIL_V2]: { width: 600, height: 987, aspectRatio: '10:16' },
  [ShowMetaDataType.DETAIL_PAGE_THUMBNAIL_V2]: { width: 1200, height: 847, aspectRatio: '12:8' },
  [ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2]: { width: 1080, height: 1080, aspectRatio: '1:1' },
};

export const getAspectRatioFromMetadata = (metadataType: ShowMetaDataType): string => {
  return THUMBNAIL_DIMENSIONS[metadataType as string]?.aspectRatio || '1:1';
};

export const getThumbnailDimensions = (metadataType: ShowMetaDataType) => {
  return THUMBNAIL_DIMENSIONS[metadataType as string] || { width: 1080, height: 1080, aspectRatio: '1:1' };
};

export const getThumbnailDisplayName = (metadataType: ShowMetaDataType): string => {
  switch (metadataType) {
    case ShowMetaDataType.BANNER_THUMBNAIL_V2:
      return 'Home Screen Banner';
    case ShowMetaDataType.WIDGET_THUMBNAIL_V2:
      return 'Home Screen Widget';
    case ShowMetaDataType.DETAIL_PAGE_THUMBNAIL_V2:
      return 'Detail Page Thumbnail';
    case ShowMetaDataType.CONTINUE_READING_THUMBNAIL_V2:
      return 'Continue Reading';
    default:
      return 'Thumbnail';
  }
};

// Convert base64 to file size in bytes
export const getBase64FileSize = (base64: string): number => {
  const padding = (base64.match(/=/g) || []).length;
  return Math.ceil((base64.length * 3) / 4) - padding;
};

// Convert file to base64
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.substring(result.indexOf(',') + 1);
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Validate file size (max 2MB)
export const validateFileSize = (file: File): boolean => {
  const maxSize = 2 * 1024 * 1024; // 2MB in bytes
  return file.size <= maxSize;
};

// Validate image dimensions
export const validateImageDimensions = (
  file: File,
  requiredWidth: number,
  requiredHeight: number
): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      resolve(img.width === requiredWidth && img.height === requiredHeight);
    };
    img.onerror = () => resolve(false);
    img.src = URL.createObjectURL(file);
  });
};
